import React, { useMemo, useState } from 'react';

import { Card, Radio, Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  ComposedChart,
  Area,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Brush,
} from 'recharts';

const CHANNEL_COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2'];

/**
 * TrendChart - 時間趨勢分析圖表
 *
 * @param {Object} props
 * @param {Array} props.timeSeries - time_series 資料
 * @param {Object} props.channelMap - 通路映射 { id: { name } }
 * @param {Object} props.metadata - 元資料 { granularity }
 */
const TrendChart = ({ timeSeries = [], channelMap = {}, metadata = {} }) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState('total');

  // 取得所有出現過的通路 ID
  const channelIds = useMemo(() => {
    const ids = new Set();
    timeSeries.forEach((bucket) => {
      Object.keys(bucket.channels || {}).forEach((id) => ids.add(id));
    });
    return Array.from(ids);
  }, [timeSeries]);

  // 合計模式：將每個時間桶的所有通路聚合
  const totalData = useMemo(() => {
    return timeSeries.map((bucket) => {
      // 優先使用後端計算的 totals
      if (bucket.totals) {
        return {
          date: bucket.date,
          revenue: bucket.totals.revenue || 0,
          orders: bucket.totals.orders || 0,
          quantity: bucket.totals.quantity || 0,
        };
      }

      // 前端聚合 fallback
      let revenue = 0;
      let orders = 0;
      let quantity = 0;
      Object.values(bucket.channels || {}).forEach((ch) => {
        revenue += ch.revenue || 0;
        orders += ch.orders || 0;
        quantity += ch.quantity || 0;
      });
      return { date: bucket.date, revenue, orders, quantity };
    });
  }, [timeSeries]);

  // 通路拆分模式：將 channels 展平為 Recharts 需要的扁平結構
  const channelData = useMemo(() => {
    return timeSeries.map((bucket) => {
      const row = { date: bucket.date };
      channelIds.forEach((id) => {
        const ch = bucket.channels?.[id] || {};
        row[`revenue_${id}`] = ch.revenue || 0;
        row[`orders_${id}`] = ch.orders || 0;
      });
      return row;
    });
  }, [timeSeries, channelIds]);

  const showBrush = timeSeries.length > 60;

  if (!timeSeries || timeSeries.length === 0) {
    return (
      <Card title={t('shared.analytics.trend_chart.title')}>
        <Empty description={t('common.empty.no_data')} />
      </Card>
    );
  }

  const cs = metadata?.currency_symbol || '$';
  const dp = metadata?.currency_decimal_places ?? 2;

  const formatRevenue = (value) => {
    if (value >= 1000000) return `${cs}${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${cs}${(value / 1000).toFixed(0)}K`;
    return `${cs}${value.toFixed(0)}`;
  };

  const tooltipFormatter = (value, name) => {
    if (name.startsWith('revenue') || name === t('shared.analytics.trend_chart.revenue')) {
      return [`${cs}${Number(value).toFixed(dp)}`, name];
    }
    return [value, name];
  };

  const renderTotalChart = () => (
    <ResponsiveContainer width="100%" height={400}>
      <ComposedChart data={totalData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="date" fontSize={12} />
        <YAxis
          yAxisId="left"
          tickFormatter={formatRevenue}
          fontSize={12}
          label={{
            value: t('shared.analytics.trend_chart.revenue'),
            angle: -90,
            position: 'insideLeft',
            style: { fontSize: 12 },
          }}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          fontSize={12}
          label={{
            value: t('shared.analytics.trend_chart.orders'),
            angle: 90,
            position: 'insideRight',
            style: { fontSize: 12 },
          }}
        />
        <Tooltip formatter={tooltipFormatter} />
        <Legend />
        <Area
          yAxisId="left"
          type="monotone"
          dataKey="revenue"
          name={t('shared.analytics.trend_chart.revenue')}
          fill="#1890ff"
          stroke="#1890ff"
          fillOpacity={0.2}
        />
        <Bar
          yAxisId="right"
          dataKey="orders"
          name={t('shared.analytics.trend_chart.orders')}
          fill="#faad14"
          barSize={20}
          opacity={0.8}
        />
        {showBrush && (
          <Brush dataKey="date" height={30} stroke="#1890ff" />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );

  const renderChannelChart = () => (
    <ResponsiveContainer width="100%" height={400}>
      <ComposedChart data={channelData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="date" fontSize={12} />
        <YAxis
          yAxisId="left"
          tickFormatter={formatRevenue}
          fontSize={12}
          label={{
            value: t('shared.analytics.trend_chart.revenue'),
            angle: -90,
            position: 'insideLeft',
            style: { fontSize: 12 },
          }}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          fontSize={12}
          label={{
            value: t('shared.analytics.trend_chart.orders'),
            angle: 90,
            position: 'insideRight',
            style: { fontSize: 12 },
          }}
        />
        <Tooltip formatter={tooltipFormatter} />
        <Legend />
        {channelIds.map((id, index) => {
          const color = CHANNEL_COLORS[index % CHANNEL_COLORS.length];
          const name = channelMap[id]?.name || `Channel ${id}`;
          return (
            <Area
              key={`revenue_${id}`}
              yAxisId="left"
              type="monotone"
              dataKey={`revenue_${id}`}
              name={`${name} ${t('shared.analytics.trend_chart.revenue')}`}
              stroke={color}
              fill={color}
              fillOpacity={0.1}
              stackId="revenue"
            />
          );
        })}
        {channelIds.map((id, index) => {
          const color = CHANNEL_COLORS[index % CHANNEL_COLORS.length];
          const name = channelMap[id]?.name || `Channel ${id}`;
          return (
            <Bar
              key={`orders_${id}`}
              yAxisId="right"
              dataKey={`orders_${id}`}
              name={`${name} ${t('shared.analytics.trend_chart.orders')}`}
              fill={color}
              barSize={12}
              stackId="orders"
              opacity={0.7}
            />
          );
        })}
        {showBrush && (
          <Brush dataKey="date" height={30} stroke="#1890ff" />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );

  return (
    <Card
      title={t('shared.analytics.trend_chart.title')}
      extra={
        <Radio.Group value={viewMode} onChange={(e) => setViewMode(e.target.value)} size="small">
          <Radio.Button value="total">
            {t('shared.analytics.trend_chart.toggle_total')}
          </Radio.Button>
          <Radio.Button value="channel">
            {t('shared.analytics.trend_chart.toggle_channels')}
          </Radio.Button>
        </Radio.Group>
      }
    >
      {viewMode === 'total' ? renderTotalChart() : renderChannelChart()}
    </Card>
  );
};

export default TrendChart;
